import { useSelector } from "@xstate/react";
import { useCallback, useMemo } from "react";
import { waitFor } from "xstate";

import { useAuth } from "~/auth/useAuth";
import { useDocsList } from "~/components/Docs/useDocsList";
import { useRouteParams } from "~/data/route";
import { useRemoteSyncActor } from "~/state";
import { getDocsWebsite } from "~/utils/docsapi";

export type SyncStatus = "status-pending" | "syncing" | "synced";
export type PublishStatus = "status-pending" | "unpublished" | "publishing" | "published";
export type SyncMode = "publish-if-unpublished" | "try-sync" | "share-sync";

export type PlanStatus =
	| "status-pending"
	| "unachievable-nothing-to-do"
	| "unachievable-not-on-synced-branch"
	| "unachievable-not-authenticated"
	| "unachievable-unauthorized"
	| "unachievable-behind-remote"
	| "achievable";

const actions = [
	"create-publish-repo",
	"initialize-git-repo",
	"commit-initial-changes",
	"commit-outstanding-changes",
	"create-remote",
	"set-tracking-branch",
	"push-to-remote",
	"publish-document",
	"revalidate-document",
] as const;

export type PlannedOutcome = null | "commit" | "push" | "publish";

export type SyncAction = (typeof actions)[number];

const useSyncAction = (
	workspaceToPublishFrom: string,
	documentIdToPublish: string,
	mode: SyncMode
) => {
	const workspaceName = workspaceToPublishFrom;
	const documentId = documentIdToPublish;

	const {
		remoteSync,
		syncPlan: { planStatus, publishStatus, actions, plannedOutcome, branch, ahead, behind },
		canExecutePlan,
		executePlan,
		canUnpublishDocument,
		unpublishDocument,
		isIdle,
		isPlanning,
		isSyncing,
	} = useRemoteSyncActor(documentId, mode);

	const { isShared, shareLink: publishUrl } = useMemo(() => {
		const isShared = publishStatus === "published";

		if (isShared) {
			return { isShared, shareLink: `${getDocsWebsite()}/d/${workspaceName}/${documentId}` };
		} else {
			return { isShared, shareLink: "" };
		}
	}, [publishStatus, workspaceName, documentId]);

	const syncStatusMessage = useSelector(remoteSync, (state) => {
		let publishStatusMessage: string | null = null;
		const syncingStateMatches = (
			...states: // TODO: Infer these types from the machine
			(
				| "idle"
				| "creatingRemoteRepo"
				| "initializingLocalRepo"
				| "committingOutstandingChanges"
				| "creatingMomentRemote"
				| "settingMomentTrackingBranch"
				| "pushingToRemote"
				| "settingRemoteRepoToPublished"
				| "revalidatingDocument"
				| "provisioningPublishUrl"
			)[]
		) => states.some((syncing) => state?.matches(syncing));

		if (syncingStateMatches("creatingMomentRemote")) {
			publishStatusMessage = "Creating remote repository...";
		} else if (syncingStateMatches("creatingRemoteRepo")) {
			publishStatusMessage = "Creating remote repository...";
		} else if (syncingStateMatches("initializingLocalRepo")) {
			publishStatusMessage = "Initializing local repository...";
		} else if (syncingStateMatches("committingOutstandingChanges")) {
			publishStatusMessage = "Committing changes...";
		} else if (syncingStateMatches("settingMomentTrackingBranch")) {
			publishStatusMessage = "Setting tracking branch...";
		} else if (syncingStateMatches("pushingToRemote")) {
			publishStatusMessage = "Pushing commits...";
		} else if (syncingStateMatches("settingRemoteRepoToPublished")) {
			publishStatusMessage = "Setting remote repository to published...";
		} else if (syncingStateMatches("revalidatingDocument")) {
			publishStatusMessage = "Revalidating document...";
		} else if (syncingStateMatches("provisioningPublishUrl")) {
			publishStatusMessage = "Provisioning publish URL...";
		}

		return publishStatusMessage;
	});

	const sync = useCallback(
		async (commitMessage: string) => {
			if (!remoteSync || !canExecutePlan) {
				return;
			}

			executePlan(commitMessage);
			await waitFor(remoteSync, (state) => state.matches("idle"));
			return;
		},
		[remoteSync, canExecutePlan, executePlan]
	);

	const unpublish = useCallback(async () => {
		if (!remoteSync || !canUnpublishDocument) {
			return;
		}

		unpublishDocument();
		await waitFor(remoteSync, (state) => state.matches("idle"));
		return;
	}, [remoteSync, canUnpublishDocument, unpublishDocument]);

	const syncStatus: SyncStatus = useMemo(() => {
		if (isSyncing) {
			return "syncing";
		} else if (isShared) {
			return "synced";
		} else {
			return "status-pending";
		}
	}, [isSyncing, isShared]);

	return useMemo(
		() => ({
			isIdle,
			isPlanning,
			isSyncing,

			planStatus,
			syncStatus,
			syncStatusMessage,
			publishStatus,
			publishUrl,

			plan: actions,
			plannedOutcome,

			canSync: canExecutePlan,
			sync,
			canUnpublish: canUnpublishDocument,
			unpublish,

			branch,
			ahead,
			behind,
		}),
		[
			isIdle,
			isPlanning,
			isSyncing,

			planStatus,
			syncStatus,
			syncStatusMessage,
			publishStatus,
			publishUrl,

			actions,
			plannedOutcome,

			canExecutePlan,
			sync,
			canUnpublishDocument,
			unpublish,

			branch,
			ahead,
			behind,
		]
	);
};

export interface BaseSyncState {
	isIdle: boolean;
	isPlanning: boolean;
	isSyncing: boolean;

	syncStatus: PublishStatus | SyncStatus;
	syncStatusMessage: string | null;

	plan: SyncAction[];
	plannedOutcome: PlannedOutcome;
	planStatus: PlanStatus;

	canSync: boolean;
	sync: (commitMessage: string) => Promise<void>;

	branch: string | undefined;
	ahead: number;
	behind: number;
}

export interface PublishIfUnpublishedSyncState extends BaseSyncState {
	publishUrl: string;

	canUnpublish: boolean;
	unpublish: () => Promise<void>;
}

export interface TryPushSyncState extends BaseSyncState {}

export interface ShareSyncState extends TryPushSyncState {
	publishUrl: string;
}

export const useSyncDocument = <
	T extends SyncMode,
	U extends T extends "publish-if-unpublished"
		? PublishIfUnpublishedSyncState
		: T extends "try-sync"
			? TryPushSyncState
			: T extends "share-sync"
				? ShareSyncState
				: never,
>(args: {
	mode: T;
}): U => {
	const { documentId } = useRouteParams();
	const {
		documents: { data: documents },
	} = useDocsList({ facet: null });

	const { user } = useAuth();

	// TOOD get workspaceToPublishFrom from the parent machine
	const workspaceName =
		documents?.find((d) => d.id === documentId)?.workspaces[0]?.name ?? user?.nickname ?? "";

	const {
		isIdle,
		isPlanning,
		isSyncing,

		syncStatus,
		planStatus,
		syncStatusMessage,
		publishStatus,
		publishUrl,

		plan,
		plannedOutcome,

		canSync,
		sync,
		canUnpublish,
		unpublish,

		branch,
		ahead,
		behind,
	} = useSyncAction(workspaceName, documentId, args.mode);

	return useMemo<U>(() => {
		if (args.mode === "publish-if-unpublished") {
			const state: PublishIfUnpublishedSyncState = {
				isIdle,
				isPlanning,
				isSyncing,

				syncStatus: publishStatus,
				syncStatusMessage,

				plan,
				plannedOutcome,
				planStatus,
				publishUrl,

				canSync,
				sync,
				canUnpublish,
				unpublish,

				branch,
				ahead,
				behind,
			};
			return state as U;
		}
		if (args.mode === "share-sync") {
			const state: ShareSyncState = {
				isIdle,
				isPlanning,
				isSyncing,

				syncStatus,
				syncStatusMessage,

				plan,
				plannedOutcome,
				planStatus,

				canSync,
				sync,

				branch,
				ahead,
				behind,

				publishUrl,
			};

			return state as U;
		}

		const state: TryPushSyncState = {
			isIdle,
			isPlanning,
			isSyncing,

			syncStatus,
			syncStatusMessage,

			plan,
			plannedOutcome,
			planStatus,

			canSync,
			sync,

			branch,
			ahead,
			behind,
		};

		return state as U;
	}, [
		args.mode,

		isIdle,
		isPlanning,
		isSyncing,

		syncStatus,
		planStatus,
		syncStatusMessage,
		publishStatus,
		publishUrl,

		plan,
		plannedOutcome,

		canSync,
		sync,
		canUnpublish,
		unpublish,

		branch,
		ahead,
		behind,
	]);
};
